import { useSyncExternalStore } from "react"
import { History } from "lucide-react"
import { cn } from "@/lib/utils"
import { Checkbox } from "../Checkbox/Checkbox"
import { relativeTime } from "./relativeTime"

/**
 * The composer's chart-context tick box. Ticked means the AI reads what the
 * user has marked on the chart whenever they send a message — a standing
 * permission, not a per-message attachment, so there is nothing to click and
 * nothing to remove. The timestamp says when the AI's view of the chart last
 * changed; the glyph spins while a chart edit is being folded in.
 */

export interface ChartContextToggleProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  /** When the context was last rebuilt or sent (ms). Null = never. */
  updatedAt?: number | null
  /** A chart edit landed and hasn't been folded into the context yet. */
  busy?: boolean
  className?: string
}

/** "2m ago" goes stale on its own, so the label needs its own re-render
 * cadence — the wall clock is an external store like any other. Snapshots are
 * bucketed so getSnapshot stays stable between ticks. */
const TICK_MS = 30_000

function subscribeToClock(onChange: () => void): () => void {
  const id = setInterval(onChange, TICK_MS)
  return () => clearInterval(id)
}

const clockBucket = () => Math.floor(Date.now() / TICK_MS)

export function ChartContextToggle({
  checked,
  onCheckedChange,
  updatedAt,
  busy = false,
  className,
}: ChartContextToggleProps) {
  const now = useSyncExternalStore(subscribeToClock, clockBucket) * TICK_MS
  const relative = updatedAt ? relativeTime(updatedAt, Math.max(now, updatedAt)) : null

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5 shrink-0 px-3 py-2 border-t border-border/40",
        className,
      )}
    >
      <Checkbox
        id="chart-context-toggle"
        checked={checked}
        onCheckedChange={(v) => onCheckedChange(v === true)}
        className="h-3.5 w-3.5 shrink-0"
      />
      <label
        htmlFor="chart-context-toggle"
        className="text-caption text-muted-foreground cursor-pointer whitespace-nowrap shrink-0"
      >
        Chart context
      </label>
      {relative && (
        <span className="text-caption text-muted-foreground/60 whitespace-nowrap shrink-0">
          · {relative}
        </span>
      )}
      <History
        size={12}
        strokeWidth={1.75}
        aria-label={busy ? "Refreshing chart context" : "Chart context up to date"}
        className={cn(
          "shrink-0 text-muted-foreground/70 transition-transform",
          busy && "animate-spin",
        )}
      />
    </div>
  )
}
